import { useEffect, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { RefreshCw, Trash2 } from 'lucide-react';
import styles from './ChatHistory.module.css';
import { useChatHistoryStore } from '../../store/chatHistoryStore';
import { useWidgetsStore } from '../../features/widgets/store/widgetsStore';
import { useAuthStore } from '../../store/authStore';
import { UserRole } from '../../enums/userRole';
import { toast } from '../../lib/toast';
import Pagination from '../../ui/pagination/Pagination';
import Checkbox from '../../ui/Checkbox/Checkbox';
import Loader from '../../ui/Loader/Loader';
import InputModal from '../../ui/inputModal/InputModal';
import ConversationDetail from './ConversationDetail';
import type { Conversation } from '../../store/chatHistoryStore';
import { hasMinRole } from '../../lib/permissions';
import { useNavigate } from 'react-router-dom';

const MAX_TOOLS_DISPLAY = 3;

export default function ChatHistory() {
  const {
    conversations, page, pageCount, loading, filters, selectedIds,
    fetchConversations, getConversation, deleteConversation, bulkDeleteConversations, clearAllConversations,
    setPage, setFilters, setSelectedIds,
  } = useChatHistoryStore();

  const { widgets, fetchWidgets } = useWidgetsStore();
  const user = useAuthStore(state => state.user);
  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const activeWidgetIds = widgets.filter(w => w.isActive).map(w => w.id);
  const displayWidgets = isSuperAdmin ? widgets : widgets.filter(w => w.isActive);

  const [search, setSearch] = useState(filters.search);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [detailConversation, setDetailConversation] = useState<Conversation | null>(null);
  const [deletingConversations, setDeletingConversations] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!user || !hasMinRole(user?.role?.id, UserRole.Admin)) {
      navigate('/');
    }
  }, [user])
  useEffect(() => {
    // Widgets already loaded by Layout; re-fetch if needed, silent for Admin
    if (widgets.length === 0) fetchWidgets({ silent: !isSuperAdmin });
  }, []);

  // Debounce search into store filter (stable reference)
  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => { setFilters({ search: value }); }, 1000),
    [],
  );
  useEffect(() => { debouncedSetSearch(search); }, [search]);
  useEffect(() => () => debouncedSetSearch.cancel(), []);

  // Fetch when page or filters change
  useEffect(() => {
    fetchConversations();
  }, [page, filters]);

  const handleSelect = (id: string, checked: boolean) => {
    setSelectedIds(checked ? [...selectedIds, id] : selectedIds.filter(i => i !== id));
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? conversations.map(c => c.id) : []);
  };

  const handleConfirmDelete = async () => {
    if (deletingConversations) return;
    try {
      setDeletingConversations(true);
      await bulkDeleteConversations(selectedIds);
    } catch {
      toast.error('Bulk delete failed');
    } finally {
      setDeletingConversations(false);
      setShowDeleteModal(false);
    }
  };

  const handleDeleteSingle = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    try {
      await deleteConversation(id);
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleClearAll = async () => {
    const selectedWidget = widgets.find(w => w.id === filters.widgetId);
    const msg = selectedWidget
      ? `Are you sure you want to clear all conversations for "${selectedWidget.name}"? This cannot be undone.`
      : 'Are you sure you want to clear ALL conversations? This cannot be undone.';
    if (!window.confirm(msg)) return;
    try {
      await clearAllConversations(filters.widgetId || undefined);
    } catch {
      toast.error('Failed to clear conversations');
    }
  };

  const handleRowClick = async (conv: Conversation) => {
    try {
      const full = await getConversation(conv.id);
      setDetailConversation(full);
    } catch {
      toast.error('Failed to load conversation details');
    }
  };

  const getStatusClass = (status: string) => {
    if (status === 'completed') return styles.statusCompleted;
    if (status === 'error') return styles.statusError;
    return styles.statusPaused;
  };

  const getUniqueTools = (invocations: Conversation['toolInvocations']) =>
    [...new Set(invocations.map(t => t.toolName))];

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>Chat History</h2>
        <div className={styles.headerActions}>
          <button className={styles.refreshBtn} onClick={fetchConversations} aria-label="Refresh conversations">
            <RefreshCw size={14} /> Refresh
          </button>
          {isSuperAdmin && (
            <button className={styles.clearAllBtn} onClick={handleClearAll} aria-label="Clear all conversations">
              <Trash2 size={14} /> Clear All
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersRow}>
        <label className={styles.filterLabel}>
          AI Agent:
          <select
            value={filters.widgetId}
            onChange={e => { setFilters({ widgetId: e.target.value }); }}
            className={styles.filterSelect}
          >
            <option value="">All AI Agents</option>
            {displayWidgets.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </label>
        <label className={styles.filterLabel}>
          Status:
          <select
            value={filters.status}
            onChange={e => { setFilters({ status: e.target.value }); }}
            className={styles.filterSelect}
          >
            <option value="">All</option>
            <option value="completed">Completed</option>
            <option value="error">Error</option>
            <option value="paused_confirmation">Paused (Confirmation)</option>
            <option value="paused_client_tools">Paused (Client Tools)</option>
          </select>
        </label>
        <label className={styles.filterLabel}>
          From:
          <input
            type="date"
            value={filters.dateFrom}
            onChange={e => { setFilters({ dateFrom: e.target.value }); }}
            className={styles.filterDate}
          />
        </label>
        <label className={styles.filterLabel}>
          To:
          <input
            type="date"
            value={filters.dateTo}
            onChange={e => { setFilters({ dateTo: e.target.value }); }}
            className={styles.filterDate}
          />
        </label>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search messages..."
          className={styles.filterSearch}
          aria-label="Search conversations"
        />
      </div>

      {(() => {
        // Admin: only show conversations from active widgets
        const displayConversations = isSuperAdmin
          ? conversations
          : conversations.filter(c => c.widgetId && activeWidgetIds.includes(c.widgetId));

        return loading ? (
          <Loader />
        ) : displayConversations.length === 0 ? (
          <div className={styles.empty}>No conversations found.</div>
        ) : (
          <>
            {/* Bulk actions - SuperAdmin only */}
            {isSuperAdmin && (
              <div className={styles.bulkActionsRow}>
                <button
                  className={styles.bulkDeleteBtn}
                  onClick={() => { if (selectedIds.length > 0) setShowDeleteModal(true); }}
                  disabled={selectedIds.length === 0}
                >
                  Delete Selected ({selectedIds.length})
                </button>
                <InputModal
                  open={showDeleteModal}
                  title="Delete Conversations"
                  confirmText="Delete"
                  cancelText="Cancel"
                  showInput={false}
                  message={`Are you sure you want to delete ${selectedIds.length} conversation(s)? This action cannot be undone.`}
                  loading={deletingConversations}
                  loadingText="Deleting..."
                  onConfirm={handleConfirmDelete}
                  onCancel={() => {
                    if (!deletingConversations) {
                      setShowDeleteModal(false);
                    }
                  }}
                />
              </div>
            )}

            {/* Table */}
            <table className={styles.table}>
              <thead>
                <tr>
                  {isSuperAdmin && (
                    <th>
                      <Checkbox
                        checked={displayConversations.length > 0 && selectedIds.length === displayConversations.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                  )}
                  <th>Date</th>
                  <th>Widget</th>
                  <th>User</th>
                  <th>Messages</th>
                  <th>Tools Used</th>
                  <th>Status</th>
                  {isSuperAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {displayConversations.map(conv => {
                  const tools = getUniqueTools(conv.toolInvocations);
                  return (
                    <tr
                      key={conv.id}
                      onClick={() => handleRowClick(conv)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(conv); } }}
                    >
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.includes(conv.id)}
                            onChange={checked => handleSelect(conv.id, checked)}
                          />
                        </td>
                      )}
                      <td>
                        <span className={styles.timestamp}>
                          {new Date(conv.createdAt).toLocaleString()}
                        </span>
                      </td>
                      <td>
                        <span className={styles.widgetBadge}>
                          {conv.widgetName || widgets.find(w => w.id === conv.widgetId)?.name || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={styles.userEmail}>
                          {conv.user?.email || conv.userId}
                        </span>
                      </td>
                      <td>
                        <span className={styles.messageCount}>{conv.messageCount}</span>
                      </td>
                      <td>
                        <div className={styles.toolsList}>
                          {tools.slice(0, MAX_TOOLS_DISPLAY).map(name => (
                            <span key={name} className={styles.toolChip}>{name}</span>
                          ))}
                          {tools.length > MAX_TOOLS_DISPLAY && (
                            <span className={styles.moreChip}>+{tools.length - MAX_TOOLS_DISPLAY}</span>
                          )}
                          {tools.length === 0 && <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>-</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${getStatusClass(conv.status)}`}>
                          {conv.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      {isSuperAdmin && (
                        <td onClick={e => e.stopPropagation()}>
                          <button
                            className={styles.deleteBtn}
                            onClick={e => handleDeleteSingle(conv.id, e)}
                            title="Delete conversation"
                            aria-label="Delete conversation"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Pagination page={page} pageCount={pageCount} setPage={setPage} />
          </>
        );
      })()}

      {/* Detail modal */}
      {detailConversation && (
        <ConversationDetail
          conversation={detailConversation}
          widgetName={detailConversation.widgetName || widgets.find(w => w.id === detailConversation.widgetId)?.name}
          onClose={() => setDetailConversation(null)}
        />
      )}
    </div>
  );
}
